import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Sparkles, Phone } from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';

const LABELS_CHAVE = {
  bom_dia: 'Mensagem de bom dia',
  boa_noite: 'Mensagem de boa noite',
  boas_vindas: 'Boas-vindas a novo membro',
};

export default function WhatsappAutomacao() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const { whatsappStatus } = useApi();
  const whatsappConectado = whatsappStatus?.conectado === true;

  const [agendamentos, setAgendamentos] = useState([]);
  const [edicoes, setEdicoes] = useState({});
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvandoChave, setSalvandoChave] = useState(null);
  const [sucessoChave, setSucessoChave] = useState(null);
  const [sugestoes, setSugestoes] = useState({});
  const [carregandoSugestao, setCarregandoSugestao] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.whatsappGrupo.listarAgendamentos();
      setAgendamentos(resp.data.data ?? []);
    } catch {
      setErro('Erro ao carregar automação do grupo. Verifique se o serviço whatsapp-grupo está rodando.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  function valorAtual(item, campo) {
    const edicao = edicoes[item.chave];
    if (edicao && edicao[campo] !== undefined) return edicao[campo];
    return item[campo];
  }

  function handleChange(chave, campo, valor) {
    setEdicoes(prev => ({
      ...prev,
      [chave]: { ...prev[chave], [campo]: valor },
    }));
  }

  function temAlteracao(item) {
    const edicao = edicoes[item.chave];
    if (!edicao) return false;
    return Object.keys(edicao).some(campo => String(edicao[campo]) !== String(item[campo]));
  }

  async function handleSalvar(item) {
    const dados = {
      texto: valorAtual(item, 'texto'),
      horario_base: valorAtual(item, 'horario_base'),
      jitter_minutos: Number(valorAtual(item, 'jitter_minutos')),
      passo_minutos: Number(valorAtual(item, 'passo_minutos')),
      ativo: Number(valorAtual(item, 'ativo')) ? 1 : 0,
      delay_segundos: Number(valorAtual(item, 'delay_segundos')),
    };

    setSalvandoChave(item.chave);
    setErro('');
    try {
      await apiService.whatsappGrupo.atualizarAgendamento(item.chave, dados);
      await carregar();
      setEdicoes(prev => {
        const { [item.chave]: _omitido, ...resto } = prev;
        return resto;
      });
      setSucessoChave(item.chave);
      setTimeout(() => setSucessoChave(null), 2000);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar automação.');
    } finally {
      setSalvandoChave(null);
    }
  }

  async function handleSugerir(item) {
    setCarregandoSugestao(item.chave);
    setErro('');
    try {
      const resp = await apiService.whatsappGrupo.sugerirTexto(valorAtual(item, 'texto'));
      setSugestoes(prev => ({ ...prev, [item.chave]: resp.data.data.sugestoes }));
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao gerar sugestões com IA.');
    } finally {
      setCarregandoSugestao(null);
    }
  }

  function escolherSugestao(chave, texto) {
    handleChange(chave, 'texto', texto);
    setSugestoes(prev => ({ ...prev, [chave]: null }));
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">

      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Whatsapp Automação</h1>
          <p className="text-sm text-gray-500">
            Mensagens automáticas do grupo de ofertas — horário e texto
            {!admin && ' (somente leitura)'}
          </p>
        </div>
      </div>

      {whatsappStatus && !whatsappConectado && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-900">WhatsApp Desconectado</p>
              <p className="text-sm text-yellow-700 mt-1">
                Você pode editar e salvar normalmente, mas as mensagens só serão enviadas quando o WhatsApp
                for reconectado nas Configurações.
              </p>
            </div>
          </div>
        </div>
      )}

      {erro && (
        <div className="mb-4 px-4 py-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg">{erro}</div>
      )}

      {loading && (
        <div className="px-5 py-12 text-center text-sm text-gray-400">Carregando...</div>
      )}

      {!loading && agendamentos.length === 0 && !erro && (
        <div className="px-5 py-12 text-center text-sm text-gray-400 bg-white border border-gray-200 rounded-xl">
          Nenhuma automação configurada.
        </div>
      )}

      <div className="space-y-4">
        {agendamentos.map(item => (
          <div key={item.chave} className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-900">
                {LABELS_CHAVE[item.chave] || item.chave}
              </h2>

              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  disabled={!admin}
                  checked={!!Number(valorAtual(item, 'ativo'))}
                  onChange={e => handleChange(item.chave, 'ativo', e.target.checked ? 1 : 0)}
                  className="rounded border-gray-300"
                />
                Ativo
              </label>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-500">Texto da mensagem</label>
                {admin && (
                  <button
                    onClick={() => handleSugerir(item)}
                    disabled={carregandoSugestao === item.chave}
                    className="flex items-center gap-1.5 text-xs text-purple-700 hover:text-purple-900 disabled:opacity-40"
                  >
                    {carregandoSugestao === item.chave
                      ? <Loader2 size={12} className="animate-spin" />
                      : <Sparkles size={12} />}
                    Sugerir texto com IA
                  </button>
                )}
              </div>

              {admin ? (
                <textarea
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
                  value={valorAtual(item, 'texto')}
                  onChange={e => handleChange(item.chave, 'texto', e.target.value)}
                />
              ) : (
                <p className="text-sm text-gray-900">{item.texto}</p>
              )}

              {item.tipo === 'evento' && (
                <p className="text-xs text-gray-400 mt-1.5">
                  Use <code className="bg-gray-100 px-1 rounded">{'{nome}'}</code> no texto — é substituído pelo nome do
                  contato no WhatsApp ou, se ele não tiver nome disponível, pelo número.
                </p>
              )}

              {sugestoes[item.chave] && (
                <div className="mt-2 space-y-1.5">
                  {sugestoes[item.chave].map((sugestao, i) => (
                    <button
                      key={i}
                      onClick={() => escolherSugestao(item.chave, sugestao)}
                      className="block w-full text-left text-sm text-gray-700 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2 hover:bg-purple-100 transition-colors"
                    >
                      {sugestao}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {item.tipo === 'evento' && (
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Atraso antes de enviar (segundos)</label>
                {admin ? (
                  <input
                    type="number"
                    min="0"
                    max="120"
                    className="w-32 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                    value={valorAtual(item, 'delay_segundos')}
                    onChange={e => handleChange(item.chave, 'delay_segundos', e.target.value)}
                  />
                ) : (
                  <span className="text-sm text-gray-900">{item.delay_segundos}s</span>
                )}
              </div>
            )}

            {item.tipo !== 'evento' && (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1.5">Horário base</label>
                    {admin ? (
                      <input
                        type="time"
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                        value={valorAtual(item, 'horario_base')}
                        onChange={e => handleChange(item.chave, 'horario_base', e.target.value)}
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{item.horario_base}</span>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1.5">Variação (min)</label>
                    {admin ? (
                      <input
                        type="number"
                        min="0"
                        max="120"
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                        value={valorAtual(item, 'jitter_minutos')}
                        onChange={e => handleChange(item.chave, 'jitter_minutos', e.target.value)}
                      />
                    ) : (
                      <span className="text-sm text-gray-900">±{item.jitter_minutos} min</span>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1.5">Passo (min)</label>
                    {admin ? (
                      <input
                        type="number"
                        min="1"
                        max="60"
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                        value={valorAtual(item, 'passo_minutos')}
                        onChange={e => handleChange(item.chave, 'passo_minutos', e.target.value)}
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{item.passo_minutos} min</span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-4">
                  Mudar o horário base só vale a partir do próximo ciclo — o disparo já agendado para hoje não muda.
                </p>
              </>
            )}

            {admin && (
              <button
                onClick={() => handleSalvar(item)}
                disabled={!temAlteracao(item) || salvandoChave === item.chave}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {salvandoChave === item.chave
                  ? <Loader2 size={13} className="animate-spin" />
                  : <Save size={13} />}
                {sucessoChave === item.chave ? 'Salvo!' : 'Salvar'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
